import { Avatar, Box, Divider, Flex, Text } from '@chakra-ui/react'
import { useEffect, useRef } from 'react'
import type { InboxMessage, SenderType } from './types'
import { getAvatarColorFromName } from '@/lib/utils/avatarColor'

const formatFull = (iso: string) => {
  const d = new Date(iso)
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const avatarBg = (type: SenderType, name: string) => {
  if (type === 'agent') return 'green.500'
  if (type === 'customer') return getAvatarColorFromName(name)
  return 'gray.400'
}

type Props = {
  threadId: string | null
  messages: InboxMessage[]
}

export const ThreadView = ({ threadId, messages }: Props) => {
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    // scroll when switching thread or when messages change (send/receive)
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [threadId, messages.length])

  if (!threadId) {
    return (
      <Box p="24px">
        <Text color="gray.500">Select a thread to view messages.</Text>
      </Box>
    )
  }

  return (
    // ✅ fill the available height given by InboxLayout
    <Box h="full" minH={0} display="flex" flexDir="column" overflow="hidden">
      {/* ✅ scroll container */}
      <Box flex="1" minH={0} overflowY="auto" overscrollBehavior="contain">
        {messages.map((m, idx) => {
          const isFirst = idx === 0

          return (
            <Box key={m.id}>
              <Flex px="20px" py="18px" gap="12px" align="flex-start">
                <Avatar
                  name={m.fromName}
                  size="sm"
                  bg={avatarBg(m.fromType, m.fromName)}
                  color="white"
                />

                <Box flex="1" minW="0">
                  <Flex justify="space-between" align="center" gap="10px">
                    <Flex align="baseline" gap="10px" minW="0">
                      <Text fontWeight="700" noOfLines={1}>
                        {m.fromName}
                      </Text>

                      {isFirst && m.fromEmail ? (
                        <Text fontSize="sm" color="gray.400" noOfLines={1}>
                          {m.fromEmail}
                        </Text>
                      ) : null}
                    </Flex>

                    <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                      {formatFull(m.createdAt)}
                    </Text>
                  </Flex>

                  <Text mt="10px" whiteSpace="pre-wrap" color="gray.800">
                    {m.body}
                  </Text>
                </Box>
              </Flex>

              <Divider borderColor="gray.100" />
            </Box>
          )
        })}

        {/* ✅ bottom anchor for autoscroll */}
        <Box ref={bottomRef} />
      </Box>
    </Box>
  )
}